import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { notificationsList, notificationsMarkAsRead, notificationsUnreadCount } from '../api'
import { Auth } from './ridersUserContext'
import { createEncryptor } from 'simple-encryptor'
import { LanguageContext } from './languageContext'
import { getCookie } from '../components/shared/Cookie'

interface NotificationContextType {
  notifications: Notification[]
  newNotificationTrigger: number
  refreshNotifications: () => Promise<void>
  markAsReadAll: () => Promise<void>
  markAsReadAllUIManipulation: () => void
  unreadNotificationCount: number
  loadMoreNotifications: () => Promise<void>
  fetchingLoadMore: boolean
}

interface MessageNotification {
  id: number
  action: {
    type: string
    name: string
    action: string
  }[]
  message: {
    tr: string
    en: string
  }
  templace_code: string
  title: {
    tr: string
    en: string
  }
}

export interface Notification {
    /**
     *
     * @type {number}
     * @memberof Notification
     */
    id: number;
    /**
     *
     * @type {boolean}
     * @memberof Notification
     */
    is_read: boolean;
    /**
     *
     * @type {Date}
     * @memberof Notification
     */
    create_time: Date;
    /**
     *
     * @type {Array<NotificationAction>}
     * @memberof Notification
     */
    actions?: Array<NotificationAction>;
    /**
     *
     * @type {string}
     * @memberof Notification
     */
    title?: string;
    /**
     *
     * @type {string}
     * @memberof Notification
     */
    message?: string;
}

export interface NotificationAction {
    /**
     *
     * @type {string}
     * @memberof NotificationAction
     */
    type: string;
    /**
     *
     * @type {string}
     * @memberof NotificationAction
     */
    name: string;
    /**
     *
     * @type {string}
     * @memberof NotificationAction
     */
    action: string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
)

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    )
  }
  return context
}

export const NotificationProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {

  const {
    accessTokenKey,
  } = useContext<any>(Auth.context);

  const { language } = useContext<any>(LanguageContext)
  const { project, loading } = useContext<any>(Auth.context);

  const [notifications, setNotifications] = useState<Notification[]>([])
  const [newNotificationTrigger, setNewNotificationTrigger] = useState(0)
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)

  const [lastFetchedNotificationPage, setLastFetchedNotificationPage] =
    useState(1)
  const [noLoadMore, setNoLoadMore] = useState(false)
  const [fetchingLoadMore, setFetchingLoadMore] = useState(false)

  const fetchNotifications = async () => {
    try {
      const data = await notificationsList(getCookie(accessTokenKey) as string, 1, 5, language === 'TR' ? 'tr' : 'en')
      console.log('setting notifications', data.results)
      setNotifications(data.results)
    } catch {
      /* empty */
    }
  }

  const fetchUnreadNotificationsCount = async () => {
    try {
      console.log('fetchUnreadNotificationsCount')
      const unreadNotificationCount =
        await notificationsUnreadCount(getCookie(accessTokenKey) as string)
      setUnreadNotificationCount(
        (unreadNotificationCount as any).unread_count
      )
    } catch {
      /* empty */
    }
  }

  const loadMoreNotifications = async () => {
    if (noLoadMore || fetchingLoadMore) return
    let notificationIds: Array<number> = []
    try {
      setFetchingLoadMore(true)

      // const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
      // await sleep(3000); // 1 saniye bekler

      const data = await notificationsList(
        getCookie(accessTokenKey) as string,
        lastFetchedNotificationPage + 1,
        5,
        language === 'TR' ? 'tr' : 'en'
      )
      setLastFetchedNotificationPage((prev) => prev + 1)
      setNotifications((prev) => {
        return [
          ...prev,
          ...data.results.filter(
            (notification : any) => !prev.find((n) => n.id === notification.id)
          )
        ]
      })
      notificationIds = data.results.map((notification : any) => notification.id)
    } catch {
      setNoLoadMore(true)
    }
    if (notificationIds.length > 0) {
      await markAsReadMany(notificationIds)
    }
    setFetchingLoadMore(false)
  }

  const encryptor = createEncryptor("simpleKeyForSimpleEncryptor");

  const encryptMessage = useCallback((message: any) => {
    if (typeof message === "string") {
      return encryptor.encrypt(message);
    } else if (typeof message === "object") {
      return encryptor.encrypt(JSON.stringify(message));
    }
    return "ERROR";
  }, []);

  const markAsReadAll = async () => {
    const unreadedNotifications = notifications.filter(
      (notification) => !notification.is_read
    )
    if (unreadedNotifications.length === 0) return
    try {
      await notificationsMarkAsRead(
        getCookie(accessTokenKey) as string,
        encryptMessage(unreadedNotifications.map(
          (notification) => notification.id
        ))
      )
    } catch {
      /* empty */
    }
  }

  const markAsReadAllUIManipulation = () => {
    setUnreadNotificationCount(
      (prev) =>
        prev -
        notifications.filter((notification) => !notification.is_read).length
    )
    setNotifications((prev) => {
      return prev.map((notification) => {
        return {
          ...notification,
          is_read: true
        }
      })
    })
  }

  const markAsRead = async (notificationId: number) => {
    try {
      await notificationsMarkAsRead(
        getCookie(accessTokenKey) as string,
        encryptMessage([notificationId]))
    } catch {
      /* empty */
    }
  }

  const markAsReadMany = async (notificationIds: number[]) => {
    try {
      await notificationsMarkAsRead(
        getCookie(accessTokenKey) as string,
        encryptMessage(notificationIds)
      )
    } catch {
      /* empty */
    }
  }

  const getWebSocketURL = (session_id: string) => {
    const host = window.location.hostname;

    let apiHost: string;
    if (host === 'localhost' || host === 'editor-staging.riders.ai') {
      apiHost = 'api-staging.riders.ai';
    } else if (host === 'editor.riders.ai') {
      apiHost = 'api.riders.ai';
    } else {
      console.warn('Bilinmeyen host, varsayılan olarak staging kullanılacak');
      apiHost = 'api-staging.riders.ai';
    }

    return `wss://${apiHost}/ws/notification/?sessionid=${session_id}`;
  };

  useEffect(() => {

    if (!accessTokenKey || loading || !project) return

    // Başlangıçta çek
    fetchNotifications()
    fetchUnreadNotificationsCount()

    // WebSocket kur
    const session_id = getCookie("sessionid") // Replace with actual session ID
    console.log(getWebSocketURL(session_id || ""))
    if (!session_id) return
    const ws = new WebSocket(getWebSocketURL(session_id))
    ws.onmessage = (message) => {
      setNewNotificationTrigger((prev) => prev + 1)
      const messageNotification = JSON.parse(
        message.data
      ) as MessageNotification
      setNotifications((prev) => {
        if (
          prev.find(
            (notification) => notification.id === messageNotification.id
          )
        ) {
          return prev
        }
        return [
          {
            id: messageNotification.id,
            title: messageNotification.title[language === 'TR' ? 'tr' : 'en'],
            message:
              messageNotification.message[language === 'TR' ? 'tr' : 'en'],
            create_time: new Date(),
            is_read: false
          },
          ...prev
        ]
      })
      setUnreadNotificationCount((prev) => prev + 1)
      if (document.hasFocus()) {
        markAsRead(messageNotification.id).then()
      }
    }

    // Cleanup
    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
    }
  }, [accessTokenKey, project, loading])

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        refreshNotifications: fetchNotifications,
        newNotificationTrigger,
        markAsReadAll,
        markAsReadAllUIManipulation,
        unreadNotificationCount,
        loadMoreNotifications,
        fetchingLoadMore
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}
